import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Loader2, ShoppingBag, X } from "lucide-react";
import { WiredConexao, WiredLojaConfig, wiredService } from "../../../services/wired";

interface WiredLojaConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: WiredLojaConfig | null;
  conexoes: WiredConexao[];
  onSuccess?: () => void;
}

const NOME_VARIAVEL_REGEX = /^[\w.-]{1,128}$/;

export function WiredLojaConfigModal({ isOpen, onClose, config, conexoes, onSuccess }: WiredLojaConfigModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [idConexao, setIdConexao] = useState("");
  const [pontos, setPontos] = useState("");
  const [elegibilidade, setElegibilidade] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setIdConexao(config?.id_conexao ?? conexoes[0]?.id ?? "");
    setPontos(config?.nome_variavel_pontos ?? "");
    setElegibilidade(config?.nome_variavel_elegibilidade ?? "");
    setAtivo(config?.ativo ?? true);
    setError("");
  }, [isOpen, config, conexoes]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!idConexao) return setError("Selecione uma conexão.");
    if (!NOME_VARIAVEL_REGEX.test(pontos.trim())) return setError("Variável de pontos inválida (letras, números, _, - e .).");
    if (elegibilidade.trim() && !NOME_VARIAVEL_REGEX.test(elegibilidade.trim())) {
      return setError("Variável de elegibilidade inválida.");
    }

    try {
      setSubmitting(true);
      await wiredService.salvarLojaConfig({
        id_conexao: idConexao,
        nome_variavel_pontos: pontos.trim(),
        nome_variavel_elegibilidade: elegibilidade.trim() || null,
        ativo,
      });
      enqueueSnackbar("Configuração da loja salva!", { variant: "success" });
      onClose();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || "Erro ao salvar configuração da loja", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              Configuração da loja
            </CardTitle>
            <CardDescription>Variáveis usadas pela loja de atributos</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0" disabled={submitting}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Conexão</Label>
              <Select value={idConexao} onValueChange={setIdConexao} disabled={submitting || !!config}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma conexão" />
                </SelectTrigger>
                <SelectContent>
                  {conexoes.map((conexao) => (
                    <SelectItem key={conexao.id} value={conexao.id}>
                      {conexao.nome} (quarto {conexao.room_id}){!conexao.possui_chave_escrita ? " - sem chave de escrita" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="loja_pontos">Variável de pontos de atributo</Label>
              <Input
                id="loja_pontos"
                value={pontos}
                onChange={(e) => {
                  setPontos(e.target.value);
                  setError("");
                }}
                placeholder="Ex: pontos_atributo"
                className="font-mono"
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="loja_elegibilidade">Variável de acesso ao Sobrevivente (opcional)</Label>
              <Input
                id="loja_elegibilidade"
                value={elegibilidade}
                onChange={(e) => {
                  setElegibilidade(e.target.value);
                  setError("");
                }}
                placeholder="Sem restrição"
                className="font-mono"
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">
                Só militares que possuem esta variável no jogo veem o menu e acessam a página Sobrevivente (status, níveis, loja, missões e estatísticas).
              </p>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label htmlFor="loja_cfg_ativo">Loja ativa</Label>
              <Switch id="loja_cfg_ativo" checked={ativo} onCheckedChange={setAtivo} disabled={submitting} />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={submitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  "Salvar"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
